import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Search,
  Loader2,
  X,
  Eye,
  Plus,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Receipt,
  Wallet,
  Calendar,
  Filter,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet, apiPost, formatMXN } from '../lib/api'

interface RefundRow {
  id: string
  orderId: string
  orderNumber: string
  customerName: string
  customerEmail: string
  orderTotal: number
  orderPaymentMethod: string
  orderPaymentStatus: string
  provider: string
  providerRef: string | null
  amount: number
  reason: string | null
  status: string
  requestedByUserId: string | null
  approvedByUserId: string | null
  errorMessage: string | null
  processedAt: string | null
  createdAt: string
  updatedAt: string
}

interface RefundsListResponse {
  total: number
  page: number
  pageSize: number
  pages: number
  totals: { pendingAmount: number; refundedAmount: number }
  data: RefundRow[]
}

const PROVIDER_OPTIONS = [
  { value: '', label: 'Todas las pasarelas' },
  { value: 'mercadopago', label: 'Mercado Pago' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'aplazo', label: 'Aplazo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'manual', label: 'Manual' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'approved', label: 'Aprobado' },
  { value: 'failed', label: 'Fallido' },
  { value: 'refunded', label: 'Reembolsado' },
]

function providerBadge(p: string) {
  const map: Record<string, string> = {
    mercadopago: 'Mercado Pago',
    paypal: 'PayPal',
    aplazo: 'Aplazo',
    transferencia: 'Transferencia',
    manual: 'Manual',
  }
  return <Badge variant="info">{map[p] || p}</Badge>
}

function statusBadge(s: string) {
  if (s === 'refunded') return <Badge variant="success">Reembolsado</Badge>
  if (s === 'approved') return <Badge variant="success">Aprobado</Badge>
  if (s === 'pending') return <Badge variant="warning">Pendiente</Badge>
  if (s === 'failed') return <Badge variant="danger">Fallido</Badge>
  return <Badge>{s}</Badge>
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

function readInitial() {
  if (typeof window === 'undefined') {
    return { page: 1, provider: '', status: '', from: '', to: '', q: '' }
  }
  const sp = new URLSearchParams(window.location.search)
  return {
    page: Math.max(1, parseInt(sp.get('page') || '1') || 1),
    provider: sp.get('provider') || '',
    status: sp.get('status') || '',
    from: sp.get('from') || '',
    to: sp.get('to') || '',
    q: sp.get('q') || '',
  }
}

export function Refunds() {
  const initial = useMemo(readInitial, [])
  const [page, setPage] = useState(initial.page)
  const [provider, setProvider] = useState(initial.provider)
  const [status, setStatus] = useState(initial.status)
  const [from, setFrom] = useState(initial.from)
  const [to, setTo] = useState(initial.to)
  const [qInput, setQInput] = useState(initial.q)
  const [q, setQ] = useState(initial.q)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showNewModal, setShowNewModal] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const sp = new URLSearchParams()
    if (page > 1) sp.set('page', String(page))
    if (provider) sp.set('provider', provider)
    if (status) sp.set('status', status)
    if (from) sp.set('from', from)
    if (to) sp.set('to', to)
    if (q) sp.set('q', q)
    const qs = sp.toString()
    window.history.replaceState(null, '', `${window.location.pathname}${qs ? '?' + qs : ''}`)
  }, [page, provider, status, from, to, q])

  const queryString = useMemo(() => {
    const sp = new URLSearchParams()
    sp.set('page', String(page))
    if (provider) sp.set('provider', provider)
    if (status) sp.set('status', status)
    if (from) sp.set('from', from)
    if (to) sp.set('to', to)
    if (q) sp.set('q', q)
    return sp.toString()
  }, [page, provider, status, from, to, q])

  const { data, isLoading, isFetching } = useQuery<RefundsListResponse>({
    queryKey: ['refunds', page, provider, status, from, to, q],
    queryFn: () => apiGet(`/api/admin/refunds?${queryString}`),
    placeholderData: (prev) => prev,
  })

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setQ(qInput.trim())
  }

  function clearFilters() {
    setQInput('')
    setQ('')
    setProvider('')
    setStatus('')
    setFrom('')
    setTo('')
    setPage(1)
  }

  const hasFilters = q || provider || status || from || to

  return (
    <>
      <Topbar
        title="Reembolsos"
        subtitle={
          data
            ? `${data.total.toLocaleString('es-MX')} refunds registrados`
            : 'Cargando…'
        }
      />
      <main className="p-6">
        {/* Resumen */}
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-ink-200 bg-white p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-ink-500">
              <Receipt className="size-3.5" /> Total
            </div>
            <div className="mt-1 text-2xl font-bold text-ink-900">
              {data ? data.total.toLocaleString('es-MX') : '—'}
            </div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-amber-700">
              <Wallet className="size-3.5" /> Pendiente / Aprobado
            </div>
            <div className="mt-1 text-2xl font-bold text-amber-900">
              {data ? formatMXN(data.totals.pendingAmount) : '—'}
            </div>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-emerald-700">
              <CheckCircle2 className="size-3.5" /> Reembolsado
            </div>
            <div className="mt-1 text-2xl font-bold text-emerald-900">
              {data ? formatMXN(data.totals.refundedAmount) : '—'}
            </div>
          </div>
        </div>

        {/* Filtros */}
        <form onSubmit={onSearch} className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative min-w-64 flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Buscar por # pedido, cliente, email o ref pasarela…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <select
            value={provider}
            onChange={(e) => {
              setProvider(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
          >
            {PROVIDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-1 rounded-lg border border-ink-200 bg-white px-2 py-1.5 text-sm">
            <Calendar className="size-3.5 text-ink-500" />
            <input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value)
                setPage(1)
              }}
              className="border-none bg-transparent text-xs outline-none"
            />
            <span className="text-ink-400">→</span>
            <input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value)
                setPage(1)
              }}
              className="border-none bg-transparent text-xs outline-none"
            />
          </div>
          <button
            type="submit"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Filter className="mr-1 inline size-3.5" /> Filtrar
          </button>
          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <X className="size-3.5" /> Limpiar
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowNewModal(true)}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            <Plus className="size-4" /> Nuevo refund manual
          </button>
        </form>

        {/* Tabla */}
        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando reembolsos…</p>
            </div>
          )}
          {data && (
            <>
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                    <Loader2 className="size-4 animate-spin text-brand-600" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-4 py-3 font-medium">Pedido</th>
                      <th className="px-4 py-3 font-medium">Cliente</th>
                      <th className="px-4 py-3 font-medium">Pasarela</th>
                      <th className="px-4 py-3 font-medium">Ref</th>
                      <th className="px-4 py-3 font-medium text-right">Monto</th>
                      <th className="px-4 py-3 font-medium">Estado</th>
                      <th className="px-4 py-3 font-medium">Fecha</th>
                      <th className="w-24 px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-12 text-center text-sm text-ink-500">
                          No se encontraron refunds con esos filtros.
                        </td>
                      </tr>
                    )}
                    {data.data.map((r) => (
                      <tr
                        key={r.id}
                        onClick={() => setSelectedId(r.id)}
                        className="cursor-pointer border-t border-ink-100 transition-colors hover:bg-ink-50"
                      >
                        <td className="px-4 py-3 font-mono text-xs font-medium text-ink-900">
                          #{r.orderNumber}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-ink-900">
                            {r.customerName || '—'}
                          </div>
                          <div className="truncate text-xs text-ink-500">
                            {r.customerEmail}
                          </div>
                        </td>
                        <td className="px-4 py-3">{providerBadge(r.provider)}</td>
                        <td className="px-4 py-3 font-mono text-xs text-ink-700">
                          {r.providerRef || '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-ink-900">
                          {formatMXN(r.amount)}
                        </td>
                        <td className="px-4 py-3">{statusBadge(r.status)}</td>
                        <td className="px-4 py-3 text-xs text-ink-700">
                          {formatDate(r.createdAt)}
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setSelectedId(r.id)}
                              className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                              title="Ver detalle"
                            >
                              <Eye className="size-4" />
                            </button>
                            {r.status === 'failed' && <RetryButton id={r.id} />}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                <div className="text-ink-500">
                  Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                  <strong className="text-ink-900">{data.pages.toLocaleString('es-MX')}</strong>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button className="rounded-md bg-brand-600 px-3 py-1 text-white">{page}</button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= data.pages}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {selectedId && <RefundDetailModal id={selectedId} onClose={() => setSelectedId(null)} />}
      {showNewModal && <NewRefundModal onClose={() => setShowNewModal(false)} />}
    </>
  )
}

function RetryButton({ id }: { id: string }) {
  const queryClient = useQueryClient()
  const mut = useMutation({
    mutationFn: () => apiPost(`/api/admin/refunds/${id}/retry`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['refunds'] })
    },
  })
  return (
    <button
      disabled={mut.isPending}
      onClick={() => mut.mutate()}
      className="rounded-md p-1.5 text-amber-600 hover:bg-amber-100 disabled:opacity-50"
      title="Reintentar refund"
    >
      {mut.isPending ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
    </button>
  )
}

interface RefundDetail {
  id: string
  orderId: string
  provider: string
  providerRef: string | null
  amount: number
  reason: string | null
  status: string
  errorMessage: string | null
  processedAt: string | null
  createdAt: string
  updatedAt: string
  order: {
    id: string
    orderNumber: string
    customerName: string
    customerEmail: string
    total: number
    paymentMethod: string
    paymentStatus: string
    status: string
    createdAt: string
  } | null
}

function RefundDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery<{ data: RefundDetail }>({
    queryKey: ['refund', id],
    queryFn: () => apiGet(`/api/admin/refunds/${id}`),
  })

  const retryMut = useMutation({
    mutationFn: () => apiPost(`/api/admin/refunds/${id}/retry`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['refunds'] })
      queryClient.invalidateQueries({ queryKey: ['refund', id] })
    },
  })

  const r = data?.data

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-950/60 p-4">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-ink-200 px-5 py-3">
          <h2 className="text-base font-semibold text-ink-900">
            Detalle refund {r ? `#${r.id}` : ''}
          </h2>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="size-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-5">
          {isLoading && (
            <div className="grid place-items-center py-12 text-ink-500">
              <Loader2 className="size-6 animate-spin" />
            </div>
          )}
          {r && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {providerBadge(r.provider)}
                {statusBadge(r.status)}
              </div>

              {r.errorMessage && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="mt-0.5 size-4 shrink-0" />
                  <span>{r.errorMessage}</span>
                </div>
              )}

              <div className="rounded-xl border border-ink-200 bg-ink-50/50 p-4">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">
                  Refund
                </h3>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-ink-500">Monto</dt>
                    <dd className="font-semibold text-ink-900">{formatMXN(r.amount)}</dd>
                  </div>
                  <div>
                    <dt className="text-ink-500">Ref pasarela</dt>
                    <dd className="font-mono text-xs text-ink-900">
                      {r.providerRef || '—'}
                    </dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-ink-500">Motivo</dt>
                    <dd className="text-ink-900">{r.reason || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-ink-500">Creado</dt>
                    <dd className="text-ink-900">{formatDate(r.createdAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-ink-500">Procesado</dt>
                    <dd className="text-ink-900">{formatDate(r.processedAt)}</dd>
                  </div>
                </dl>
              </div>

              {r.order && (
                <div className="rounded-xl border border-ink-200 bg-ink-50/50 p-4">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">
                    Pedido
                  </h3>
                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-ink-500"># Pedido</dt>
                      <dd className="font-mono font-semibold text-ink-900">
                        #{r.order.orderNumber}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-ink-500">Total</dt>
                      <dd className="font-semibold text-ink-900">
                        {formatMXN(r.order.total)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-ink-500">Cliente</dt>
                      <dd className="text-ink-900">{r.order.customerName}</dd>
                    </div>
                    <div>
                      <dt className="text-ink-500">Email</dt>
                      <dd className="break-all text-ink-900">{r.order.customerEmail}</dd>
                    </div>
                    <div>
                      <dt className="text-ink-500">Método pago</dt>
                      <dd className="capitalize text-ink-900">{r.order.paymentMethod}</dd>
                    </div>
                    <div>
                      <dt className="text-ink-500">Status pago</dt>
                      <dd className="capitalize text-ink-900">{r.order.paymentStatus}</dd>
                    </div>
                  </dl>
                </div>
              )}

              {r.status === 'failed' && (
                <button
                  disabled={retryMut.isPending}
                  onClick={() => retryMut.mutate()}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {retryMut.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <RefreshCw className="size-4" />
                  )}
                  Reintentar refund
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface OrderPickerItem {
  id: string
  orderNumber: string
  customerName: string
  customerEmail: string
  total: number
  paymentMethod: string
  paymentStatus: string
}

function NewRefundModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [orderQ, setOrderQ] = useState('')
  const [selected, setSelected] = useState<OrderPickerItem | null>(null)
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [provider, setProvider] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const orderQuery = useQuery({
    queryKey: ['order-picker', orderQ],
    queryFn: () =>
      apiGet<{ data: Array<Record<string, unknown>> }>(
        `/api/admin/pedidos?q=${encodeURIComponent(orderQ)}&page=1`,
      ).then((res) => ({
        // Normalizar — el endpoint /api/admin/pedidos usa otros nombres
        // (nrodePedido, cliente, email, fp, paymentStatus).
        data: (res.data || []).map((o) => ({
          id: String(o.id ?? ''),
          orderNumber: String(o.nrodePedido ?? o.orderNumber ?? o.order_number ?? ''),
          customerName: String(o.cliente ?? o.customerName ?? o.customer_name ?? ''),
          customerEmail: String(o.email ?? o.customerEmail ?? o.customer_email ?? ''),
          total: Number(o.total ?? 0),
          paymentMethod: String(o.fp ?? o.paymentMethod ?? o.payment_method ?? ''),
          paymentStatus: String(o.paymentStatus ?? o.payment_status ?? ''),
        })) as OrderPickerItem[],
      })),
    enabled: orderQ.length >= 2 && !selected,
    placeholderData: (prev) => prev,
  })

  const createMut = useMutation({
    mutationFn: () =>
      apiPost<{ ok: boolean; status: string; errorMessage: string | null }>(
        '/api/admin/refunds',
        {
          orderId: selected?.id,
          amount: parseFloat(amount) || 0,
          reason: reason || undefined,
          provider: provider || undefined,
        },
      ),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['refunds'] })
      if (res.ok) {
        setSuccess(`Refund creado (status: ${res.status}).`)
        setTimeout(() => onClose(), 1500)
      } else {
        setError(res.errorMessage || 'El refund quedó en estado failed.')
      }
    },
    onError: (e: Error) => {
      setError(e.message || 'Error al crear refund')
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    if (!selected) {
      setError('Selecciona un pedido')
      return
    }
    const n = parseFloat(amount)
    if (!n || n <= 0) {
      setError('Monto inválido')
      return
    }
    createMut.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-950/60 p-4">
      <div className="flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-ink-200 px-5 py-3">
          <h2 className="text-base font-semibold text-ink-900">Nuevo refund manual</h2>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="size-4" />
          </button>
        </header>
        <form onSubmit={handleSubmit} className="flex-1 space-y-4 overflow-y-auto p-5">
          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
              <span>{success}</span>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-700">
              Pedido
            </label>
            {selected ? (
              <div className="flex items-center justify-between rounded-lg border border-ink-200 bg-ink-50 p-3 text-sm">
                <div>
                  <div className="font-mono font-semibold text-ink-900">
                    #{selected.orderNumber}
                  </div>
                  <div className="text-xs text-ink-500">
                    {selected.customerName} — {selected.customerEmail} —{' '}
                    {formatMXN(selected.total)}
                  </div>
                  <div className="text-xs text-ink-500">
                    Pago: {selected.paymentMethod || 'desconocido'} (
                    {selected.paymentStatus})
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100"
                >
                  <X className="size-4" />
                </button>
              </div>
            ) : (
              <>
                <input
                  type="search"
                  value={orderQ}
                  onChange={(e) => setOrderQ(e.target.value)}
                  placeholder="Buscar # pedido, cliente o email…"
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
                {orderQ.length >= 2 && (
                  <div className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-ink-200">
                    {orderQuery.isLoading && (
                      <div className="p-3 text-center text-xs text-ink-500">
                        Buscando…
                      </div>
                    )}
                    {orderQuery.data?.data.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => {
                          setSelected(o)
                          if (o.total > 0) setAmount(String(o.total))
                        }}
                        className="block w-full border-b border-ink-100 px-3 py-2 text-left text-sm hover:bg-ink-50"
                      >
                        <span className="font-mono font-semibold">
                          #{o.orderNumber}
                        </span>{' '}
                        — {o.customerName} ({formatMXN(o.total)})
                      </button>
                    ))}
                    {orderQuery.data?.data.length === 0 && (
                      <div className="p-3 text-center text-xs text-ink-500">
                        Sin resultados
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ink-700">
                Monto a reembolsar
              </span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                required
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ink-700">
                Pasarela (auto si vacío)
              </span>
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
              >
                <option value="">Auto-detectar</option>
                {PROVIDER_OPTIONS.filter((p) => p.value).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-ink-700">Razón</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={255}
              placeholder="Motivo del reembolso…"
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
            />
          </label>

          <div className="flex justify-end gap-2 border-t border-ink-100 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMut.isPending || !selected}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {createMut.isPending && <Loader2 className="size-4 animate-spin" />}
              Procesar refund
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
